/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Enums, ExtensionManager, MODULE_TYPES, log } from '@ohif/core';
//
import { extensionManager } from '../App';
import { REPORTS, getStudyDateMax } from '../../../custom/constant';
import { authFetch } from '../../../custom/Utils/authFetch';
import {
  getToken,
  getRefreshToken,
  getAccessTokenExpiryMs,
  logoutAndWipeBrowser,
} from '../../../custom/Utils/authUtils';
import { refreshAccessToken } from '../../../custom/Utils/refreshAccessToken';
import { useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import useSearchParams from '../hooks/useSearchParams';

/**
 * Determines if two React Router location objects are the same.
 */
const areLocationsTheSame = (location0, location1) => {
  return (
    location0.pathname === location1.pathname &&
    location0.search === location1.search &&
    location0.hash === location1.hash
  );
};

/**
 * Uses route properties to determine the data source that should be passed
 * to the child layout template. In some instances, initiates requests and
 * passes data as props.
 *
 * @param {object} props
 * @param {function} props.children - Layout Template React Component
 */
function DataSourceWrapper(props: withAppTypes) {
  const { servicesManager } = props;
  const dispatch = useDispatch();
  const { children: LayoutTemplate, ...rest } = props;
  const params = useParams();
  const location = useLocation();
  const lowerCaseSearchParams = useSearchParams({ lowerCaseKeys: true });
  const query = useSearchParams();
  // Route props --> studies.mapParams
  // mapParams --> studies.search
  // studies.search --> studies.processResults
  // studies.processResults --> <LayoutTemplate studies={} />
  // But only for LayoutTemplate type of 'list'?
  // Or no data fetching here, and just hand down my source
  const STUDIES_LIMIT = 101;
  const DEFAULT_DATA = {
    studies: [],
    total: 0,
    resultsPerPage: 25,
    pageNumber: 1,
    location: 'Not a valid location, causes first load to occur',
  };

  const getInitialDataSourceName = useCallback(() => {
    // TODO - get the variable from the props all the time...
    let dataSourceName = lowerCaseSearchParams.get('datasources');

    if (!dataSourceName && window.config.defaultDataSourceName) {
      return '';
    }

    if (!dataSourceName) {
      // Gets the first defined datasource with the right name
      // Mostly for historical reasons - new configs should use the defaultDataSourceName
      const dataSourceModules = extensionManager.modules[MODULE_TYPES.DATA_SOURCE];
      // TODO: Good usecase for flatmap?
      const webApiDataSources = dataSourceModules.reduce((acc, curr) => {
        const mods = [];
        curr.module.forEach(mod => {
          if (mod.type === 'webApi') {
            mods.push(mod);
          }
        });
        return acc.concat(mods);
      }, []);
      dataSourceName = webApiDataSources
        .map(ds => ds.name)
        .find(it => extensionManager.getDataSources(it)?.[0] !== undefined);
    }

    return dataSourceName;
  }, []);

  const [isDataSourceInitialized, setIsDataSourceInitialized] = useState(false);

  // The path to the data source to be used in the URL for a mode (e.g. mode/dataSourcePath?StudyIntanceUIDs=1.2.3)
  const [dataSourcePath, setDataSourcePath] = useState(() => {
    const dataSourceName = getInitialDataSourceName();
    return dataSourceName ? `/${dataSourceName}` : '';
  });

  const [dataSource, setDataSource] = useState(() => {
    const dataSourceName = getInitialDataSourceName();

    if (!dataSourceName) {
      return extensionManager.getActiveDataSource()[0];
    }

    const dataSource = extensionManager.getDataSources(dataSourceName)?.[0];
    if (!dataSource) {
      throw new Error(`No data source found for ${dataSourceName}`);
    }

    return dataSource;
  });

  const [data, setData] = useState(DEFAULT_DATA);
  const [isLoading, setIsLoading] = useState(false);
  // Guards against re-entering getData() for a request that is already in flight.
  // The effect writes `data`/`isLoading` (its own deps), so setState inside getData
  // re-runs the effect before the async query resolves; without this guard, a target
  // page whose `isSamePage` is still false (e.g. deep-linking to ?pageNumber=3) fires
  // an unbounded storm of identical studies?limit=101 XHRs until the first query lands.
  const inFlightKeyRef = useRef<string | null>(null);
  // Navigation is asynchronous from React's point of view. Avoid a second
  // DICOMweb request in the small interval before a session-ending redirect
  // unmounts this route.
  const sessionEndingRef = useRef(false);

  const endSession = useCallback(() => {
    if (sessionEndingRef.current) {
      return;
    }
    sessionEndingRef.current = true;
    void logoutAndWipeBrowser(dispatch);
  }, [dispatch]);

  /**
   * The effect to initialize the data source whenever it changes. Similar to
   * whenever a different Mode is entered, the Mode's data source is initialized, so
   * too this DataSourceWrapper must initialize its data source whenever a different
   * data source is activated. Furthermore, a data source might be initialized
   * several times as it gets activated/deactivated because the location URL
   * might change and data sources initialize based on the URL.
   */
  useEffect(() => {
    const initializeDataSource = async () => {
      await dataSource.initialize({ params, query });
      setIsDataSourceInitialized(true);
    };

    initializeDataSource();
  }, [dataSource]);

  useEffect(() => {
    const dataSourceChangedCallback = () => {
      setIsLoading(false);
      setIsDataSourceInitialized(false);
      setDataSourcePath('');
      setDataSource(extensionManager.getActiveDataSource()[0]);
      // Setting data to DEFAULT_DATA triggers a new query just like it does for the initial load.
      setData(DEFAULT_DATA);
    };

    const sub = extensionManager.subscribe(
      ExtensionManager.EVENTS.ACTIVE_DATA_SOURCE_CHANGED,
      dataSourceChangedCallback
    );
    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    if (!isDataSourceInitialized) {
      return;
    }

    const queryFilterValues = _getQueryFilterValues(location.search, STUDIES_LIMIT);

    // 204: no content
    async function getData() {
      if (sessionEndingRef.current) {
        return;
      }

      setIsLoading(true);
      setData(prev => ({ ...prev, studies: [], total: 0 }));

      // Browsers suspend timers while a laptop sleeps. Make this request wait
      // for a near-expired app session to refresh instead of racing the
      // TokenRefreshManager wake handler. The refresh utility is single-flight,
      // so concurrent callers share one safe refresh request.
      const appExp = getAccessTokenExpiryMs();
      if (!getToken()) {
        endSession();
        return;
      }
      const canRefreshAppSession = Boolean(getRefreshToken());
      if (appExp !== null && appExp - Date.now() <= 60_000 && canRefreshAppSession) {
        const refreshedAppToken = await refreshAccessToken();
        if (!refreshedAppToken) {
          endSession();
          return;
        }
      } else if (appExp !== null && appExp <= Date.now()) {
        // Legacy sessions without a refresh token remain usable until their
        // actual expiry; once expired they must start a new login.
        endSession();
        return;
      }

      // DICOMweb now goes through our backend proxy, authenticated by the app login
      // token (above) with per-clinic scope enforced server-side. There is no separate
      // PACS token to refresh any more, so the query just proceeds on the app session.

      log.time(Enums.TimingEnum.SEARCH_TO_LIST);
      let studies = [];
      let total = 0;

      const hasCouchFilters =
        (Array.isArray(queryFilterValues.reportStatus) && queryFilterValues.reportStatus.length) ||
        (Array.isArray(queryFilterValues.reportType) && queryFilterValues.reportType.length) ||
        !!queryFilterValues.studyMarkings ||
        !!queryFilterValues.teachingFile;

      // "Draft" must include studies that have NO report yet (implicit Draft). The
      // report-UID federated path only knows studies that HAVE a report, so when the
      // selection includes Draft we instead take the full DICOM universe and drop only
      // the studies whose report status is NOT selected. Works for Draft alone and for
      // multi-select like Draft + Finalized. Scoped to pure status filters.
      const selectedStatuses = Array.isArray(queryFilterValues.reportStatus)
        ? queryFilterValues.reportStatus
        : [];
      const includesDraft = selectedStatuses.some(s => /^draft$/i.test(String(s)));
      const onlyStatusFilter =
        !queryFilterValues.studyMarkings &&
        !queryFilterValues.teachingFile &&
        !(Array.isArray(queryFilterValues.reportType) && queryFilterValues.reportType.length);

      if (includesDraft && onlyStatusFilter) {
        try {
          const hospitalId =
            localStorage.getItem('hospitalId') || sessionStorage.getItem('hospitalId');
          const ALL_STATUSES = ['Draft', 'Preliminary', 'Finalized'];
          const excludeStatuses = ALL_STATUSES.filter(
            s => !selectedStatuses.some(sel => String(sel).toLowerCase() === s.toLowerCase())
          );
          let excludeUIDs: string[] = [];
          if (excludeStatuses.length) {
            const exResp = await authFetch(`${REPORTS.SEARCH_STUDY_UIDS}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                hospitalId,
                pageNumber: 1,
                resultsPerPage: 100000,
                reportStatus: excludeStatuses,
                reportType: [],
                studyMarkings: '',
                teachingFile: '',
              }),
            });
            if (exResp.ok) {
              const exJson = await exResp.json();
              excludeUIDs = exJson.studyInstanceUIDs || [];
            }
          }
          const all = await dataSource.query.studies.search(queryFilterValues);
          const excludeSet = new Set(excludeUIDs);
          studies = (all || []).filter(s => !excludeSet.has(s.studyInstanceUid));
          total = studies.length;
        } catch (e) {
          console.warn('Draft-inclusive search failed, falling back to DICOM-only', e);
          studies = await dataSource.query.studies.search(queryFilterValues);
          total = studies.length;
        }
      } else if (hasCouchFilters) {
        try {
          const hospitalId =
            localStorage.getItem('hospitalId') || sessionStorage.getItem('hospitalId');
          const resp = await authFetch(`${REPORTS.SEARCH_STUDY_UIDS}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              hospitalId,
              pageNumber: queryFilterValues.pageNumber,
              resultsPerPage: queryFilterValues.resultsPerPage,
              reportStatus: queryFilterValues.reportStatus || [],
              reportType: queryFilterValues.reportType || [],
              studyMarkings: queryFilterValues.studyMarkings || '',
              teachingFile: queryFilterValues.teachingFile || '',
              // Optional DICOM filters to pre-restrict on backend if supported
              patientName: queryFilterValues.patientName,
              patientId: queryFilterValues.patientId,
              startDate: queryFilterValues.startDate,
              endDate: queryFilterValues.endDate,
              modalitiesInStudy: queryFilterValues.modalitiesInStudy,
            }),
          });

          if (resp.ok) {
            const { total: federatedTotal = 0, studyInstanceUIDs = [] } = await resp.json();

            total = federatedTotal;

            if (studyInstanceUIDs.length) {
              // Fetch the DICOM studies for these UIDs
              studies = await dataSource.query.studies.search({
                ...queryFilterValues,
                studyInstanceUid: studyInstanceUIDs,
                // limit only to the number of UIDs provided
                limit: Math.min(101, studyInstanceUIDs.length),
                offset: 0,
              });
            } else {
              studies = [];
            }
          } else {
            // Fallback to existing behavior if federated endpoint is unavailable
            studies = await dataSource.query.studies.search(queryFilterValues);
            total = studies.length;
          }
        } catch (e) {
          console.warn('Federated search failed, falling back to DICOM-only search', e);
          studies = await dataSource.query.studies.search(queryFilterValues);
          total = studies.length;
        }
      } else {
        // DICOM-only search
        studies = await dataSource.query.studies.search(queryFilterValues);
        total = studies.length;
      }

      setData({
        studies: studies || [],
        total,
        resultsPerPage: queryFilterValues.resultsPerPage,
        pageNumber: queryFilterValues.pageNumber,
        location,
      });
      log.timeEnd(Enums.TimingEnum.SCRIPT_TO_VIEW);
      log.timeEnd(Enums.TimingEnum.SEARCH_TO_LIST);

      setIsLoading(false);
    }

    try {
      // Cache invalidation :thinking:
      // - Anytime change is not just next/previous page
      // - And we didn't cross a result offset range
      const isSamePage = data.pageNumber === queryFilterValues.pageNumber;
      const previousOffset =
        Math.floor((data.pageNumber * data.resultsPerPage) / STUDIES_LIMIT) * (STUDIES_LIMIT - 1);
      const newOffset =
        Math.floor(
          (queryFilterValues.pageNumber * queryFilterValues.resultsPerPage) / STUDIES_LIMIT
        ) *
        (STUDIES_LIMIT - 1);
      // Simply checking data.location !== location is not sufficient because even though the location href (i.e. entire URL)
      // has not changed, the React Router still provides a new location reference and would result in two study queries
      // on initial load. Alternatively, window.location.href could be used.
      const isLocationUpdated =
        typeof data.location === 'string' || !areLocationsTheSame(data.location, location);
      const isDataInvalid =
        !isSamePage || (!isLoading && (newOffset !== previousOffset || isLocationUpdated));

      // Identifies the request the effect wants to make. While a getData() for this
      // exact key is already running, skip re-launching it — the effect will re-run
      // several times from getData's own setState before the query resolves.
      const requestKey = `${location.search}|${queryFilterValues.pageNumber}|${queryFilterValues.resultsPerPage}`;

      if (isDataInvalid && inFlightKeyRef.current !== requestKey) {
        inFlightKeyRef.current = requestKey;
        getData()
          .then(() => {
            // Success: getData set data.pageNumber, so isDataInvalid is now false
            // and the effect won't re-fire. Clear the key so a genuinely new
            // request (or a later re-query of this key) can run.
            if (inFlightKeyRef.current === requestKey) {
              inFlightKeyRef.current = null;
            }
          })
          .catch(e => {
            // FAILURE: getData threw before updating data.pageNumber, so
            // isDataInvalid stays true. Deliberately DO NOT clear inFlightKeyRef —
            // leaving it === requestKey blocks the effect from re-firing the same
            // failing query, which would otherwise hammer a down data source with
            // an unbounded XHR storm. Retry happens when the key changes (new
            // page/filter/search) or on reload.
            console.error(e);
            setIsLoading(false);

            // If the session is over (no token, or the access token has expired),
            // the request failed because the user is no longer authenticated — log
            // them straight out instead of showing the misleading "Data Source
            // Connection Error" to a clinician. A valid session with a genuinely
            // unreachable data source still falls through to the modal below.
            const expMs = getAccessTokenExpiryMs();
            const sessionOver = !getToken() || (expMs !== null && expMs <= Date.now());
            if (sessionOver) {
              endSession();
              return;
            }

            const { configurationAPI, friendlyName } = dataSource.getConfig();
            // If there is a data source configuration API, then the Worklist will popup the dialog to attempt to configure it
            // and attempt to resolve this issue.
            if (configurationAPI) {
              return;
            }

            servicesManager.services.uiModalService.show({
              title: 'Data Source Connection Error',
              containerDimensions: 'w-1/2',
              content: () => {
                return (
                  <div>
                    <p className="text-red-600">Error: {e.message}</p>
                    <p>
                      Please ensure the following data source is configured correctly or is running:
                    </p>
                    <div className="mt-2 font-bold">{friendlyName}</div>
                  </div>
                );
              },
            });
          });
      }
    } catch (ex) {
      console.warn(ex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, location, params, isLoading, setIsLoading, dataSource, isDataSourceInitialized]);
  // queryFilterValues

  // TODO: Better way to pass DataSource?
  return (
    <LayoutTemplate
      {...rest}
      data={data.studies}
      dataPath={dataSourcePath}
      dataTotal={data.total}
      dataSource={dataSource}
      isLoadingData={isLoading}
      // To refresh the data, simply reset it to DEFAULT_DATA which invalidates it and triggers a new query to fetch the data.
      onRefresh={() => setData(DEFAULT_DATA)}
    />
  );
}

DataSourceWrapper.propTypes = {
  /** Layout Component to wrap with a Data Source */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
};

export default DataSourceWrapper;

/**
 * Duplicated in `workList`
 * Need generic that can be shared? Isn't this what qs is for?
 * @param {*} query
 */
function _getQueryFilterValues(query, queryLimit) {
  query = new URLSearchParams(query);
  const newParams = new URLSearchParams();
  for (const [key, value] of query) {
    newParams.set(key.toLowerCase(), value);
  }
  query = newParams;

  const pageNumber = _tryParseInt(query.get('pagenumber'), 1);
  const resultsPerPage = _tryParseInt(query.get('resultsperpage'), 25);

  const queryFilterValues = {
    // DCM
    patientId: query.get('mrn'),
    patientName: query.get('patientname'),
    studyDescription: query.get('description'),
    modalitiesInStudy: query.get('modalities') && query.get('modalities').split(','),
    accessionNumber: query.get('accession'),
    //
    startDate: query.get('startdate'),
    endDate: query.get('enddate'),
    page: _tryParseInt(query.get('page'), undefined),
    pageNumber,
    resultsPerPage,
    // Rarely supported server-side
    sortBy: query.get('sortby'),
    sortDirection: query.get('sortdirection'),
    // Offset...
    offset: Math.floor((pageNumber * resultsPerPage) / queryLimit) * (queryLimit - 1),
    config: query.get('configurl'),
    // CouchDB-backed filters
    reportStatus: query.get('reportstatus') ? query.get('reportstatus').split(',') : [],
    reportType: query.get('reporttype') ? query.get('reporttype').split(',') : [],
    studyMarkings: query.get('studymarkings') || '',
    teachingFile: query.get('teachingfile') || '',
  };

  // patientName: good
  // studyDescription: good
  // accessionNumber: good

  // Staging demo clinics (e.g. Bond Radiology) carry a `studyDateMax` on their Pacs doc
  // so the worklist is frozen to studies whose lite images are guaranteed synced. Clamp
  // the upper StudyDate bound to it (keep the user's filter if it's already more
  // restrictive). No-op when the field is absent — i.e. always on production.
  const studyDateMax = getStudyDateMax();
  if (studyDateMax) {
    const currentEnd = queryFilterValues.endDate
      ? String(queryFilterValues.endDate).replace(/[^0-9]/g, '')
      : null;
    if (!currentEnd || currentEnd > studyDateMax) {
      queryFilterValues.endDate = studyDateMax;
    }
  }

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;

  function _tryParseInt(str, defaultValue) {
    let retValue = defaultValue;
    if (str !== null) {
      if (str.length > 0) {
        if (!isNaN(str)) {
          retValue = parseInt(str);
        }
      }
    }
    return retValue;
  }
}
